import { useMemo, useState } from 'react';
import { FormField, FormSelect } from '@practium/ui';
import {
	CLASSIFICATION_LABELS,
	CLASSIFICATION_ORDER,
	PLANTING_FORM_LABELS,
} from '../../constants/parameters.constants';
import { useTableList } from '../../hooks/useTableList';
import type { PlantingForm, ReferenceSectionProps, StateReferenceDemandP2O5 } from '../../types/parameters.types';
import { formatNumber } from '../../utils/format';
import { ValuesFormModal } from '../Forms/ValuesFormModal';
import { type ReferenceColumn } from '../Tables/ReferenceTable';
import { SectionList } from '../Tables/SectionList';
import tableStyles from '../Tables/ParamsTable.module.css';

type ModalState = { type: 'closed' } | { type: 'open'; item: StateReferenceDemandP2O5 | null };

const FORM_OPTIONS = (Object.keys(PLANTING_FORM_LABELS) as PlantingForm[]).map((value) => ({
	value,
	label: PLANTING_FORM_LABELS[value],
}));

const COLUMNS: ReferenceColumn<StateReferenceDemandP2O5>[] = [
	{
		key: 'form',
		header: 'Forma de plantio',
		render: (item) => <span className={tableStyles.cellPrimary}>{PLANTING_FORM_LABELS[item.form] ?? item.form}</span>,
	},
	...CLASSIFICATION_ORDER.map((classification) => ({
		key: classification,
		header: CLASSIFICATION_LABELS[classification],
		render: (item: StateReferenceDemandP2O5) => (
			<span className={tableStyles.cellValue}>
				{formatNumber(item.fixedClasses.find((dose) => dose.classification === classification)?.maxValue)}
			</span>
		),
	})),
];

const FIELDS = CLASSIFICATION_ORDER.map((classification) => ({
	key: classification,
	label: CLASSIFICATION_LABELS[classification],
}));

export const PlantingP2O5Section = ({ reference, isSaving, onSave, onConfirmDelete }: ReferenceSectionProps) => {
	const [modal, setModal] = useState<ModalState>({ type: 'closed' });
	const [form, setForm] = useState<PlantingForm>('cova');
	const list = useTableList({ items: reference.demandP2O5 });

	const availableOptions = useMemo(
		() => FORM_OPTIONS.filter((option) => !reference.demandP2O5.some((item) => item.form === option.value)),
		[reference.demandP2O5]
	);

	const openNew = () => {
		setForm((availableOptions[0]?.value as PlantingForm) ?? 'cova');
		setModal({ type: 'open', item: null });
	};

	const handleSubmit = (editing: StateReferenceDemandP2O5 | null, values: Record<string, number>) => {
		const fixedClasses = CLASSIFICATION_ORDER.map((classification) => ({
			classification,
			maxValue: values[classification],
		}));
		const demandP2O5 = editing
			? reference.demandP2O5.map((item) => (item.form === editing.form ? { ...item, fixedClasses } : item))
			: [...reference.demandP2O5, { form, fixedClasses }];
		return onSave({ demandP2O5 }, 'Doses de P₂O₅ salvas com sucesso!');
	};

	const handleDelete = async (target: StateReferenceDemandP2O5) => {
		if (await onConfirmDelete(`as doses de ${PLANTING_FORM_LABELS[target.form]}`)) {
			await onSave(
				{ demandP2O5: reference.demandP2O5.filter((item) => item.form !== target.form) },
				'Doses de P₂O₅ excluídas com sucesso!'
			);
		}
	};

	return (
		<>
			<SectionList
				list={list}
				columns={COLUMNS}
				getRowId={(item) => item.form}
				unit={['forma de plantio', 'formas de plantio']}
				actionLabel={availableOptions.length > 0 ? 'Nova forma de plantio' : undefined}
				onAction={openNew}
				onEdit={(item) => setModal({ type: 'open', item })}
				onDelete={handleDelete}
			/>
			{modal.type === 'open' && (
				<ValuesFormModal
					title={modal.item ? `Editar doses: ${PLANTING_FORM_LABELS[modal.item.form]}` : 'Nova forma de plantio'}
					subtitle="Dose de P₂O₅ por classificação do fósforo no solo."
					sectionTitle="Doses"
					fields={FIELDS}
					initialValues={Object.fromEntries(
						CLASSIFICATION_ORDER.map((classification) => [
							classification,
							modal.item?.fixedClasses.find((dose) => dose.classification === classification)?.maxValue,
						])
					)}
					isSaving={isSaving}
					onSubmit={(values) => handleSubmit(modal.item, values)}
					onClose={() => setModal({ type: 'closed' })}
				>
					{!modal.item && (
						<FormField label="Forma de plantio" required>
							<FormSelect value={form} options={availableOptions} onChange={(value) => setForm(value as PlantingForm)} />
						</FormField>
					)}
				</ValuesFormModal>
			)}
		</>
	);
};
